import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import Button from 'components/Elements/Button';
import { FormWrapper, StyledError } from 'components/lib/Formik';
import { createComment } from 'utils/api/comments';
import { CREATE_COMMENT_RECAPTCHA } from 'utils/constants';

const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 25px;
`;

const Input = styled.input`
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 1rem;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.midGrey};
`;

const TextArea = styled.textarea`
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 1rem;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.midGrey};
`;

const CommentForm = ({ onSubmit, ripspot }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <Formik
      enableReinitialize
      initialValues={{ name: '', email: '', message: '' }}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .max(255)
          .required('This field is required'),
        email: Yup.string()
          .email('The email provided should be a valid email address')
          .max(255)
          .required('This field is required'),
        message: Yup.string().required('This field is required'),
        submit: null,
      })}
      onSubmit={async (values, { setErrors, setStatus }) => {
        try {
          const recaptchaToken = await executeRecaptcha(
            CREATE_COMMENT_RECAPTCHA,
          );

          await createComment(
            {
              ripspotId: ripspot.id,
              name: values.name,
              email: values.email,
              message: values.message,
            },
            recaptchaToken,
          );

          setStatus({ success: true });
          // Close modal and fetch comments again.
          onSubmit();
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
            <FormWrapper>
              <Input
                name="name"
                placeholder="Name"
                touched={touched}
                errors={errors}
                value={values.name}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <StyledError error={touched.name && errors.name} />
              <Input
                name="email"
                placeholder="Email Address"
                touched={touched}
                errors={errors}
                value={values.email}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <StyledError error={touched.email && errors.email} />
              <TextArea
                name="message"
                placeholder="Message"
                touched={touched}
                value={values.message}
                onBlur={handleBlur}
                onChange={handleChange}
                rows={4}
              />
              <StyledError error={touched.message && errors.message} />
              <Controls>
                <Button light type="submit">
                  Submit
                </Button>
              </Controls>
            </FormWrapper>
          </form>
        );
      }}
    </Formik>
  );
};

CommentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default CommentForm;
